// Internal staff/admin-only production readiness checklist.
// Reads a server-side status snapshot so we don't leak env values to the browser.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCircle2, Circle, Loader2, ShieldAlert, AlertCircle } from "lucide-react";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import {
  AdminShell,
  AdminPageHeader,
  AdminCard,
  AdminCardHeader,
  AdminButton,
  AdminBadge,
} from "@/components/admin";
import { getLaunchChecklist } from "@/lib/launch-checklist.functions";
import { runMonitoringTest, type MonitoringTestResult } from "@/lib/monitoring-test.functions";
import { runOcrDiagnostic, type OcrDiagnosticResult } from "@/lib/ocr-diagnostic.functions";
import {
  runLocationDiagnostic,
  type LocationDiagnosticResult,
} from "@/lib/location-diagnostic.functions";
import {
  runEmailDiagnostic,
  type EmailDiagnosticResult,
} from "@/lib/email-diagnostic.functions";
import {
  runAnalyticsTest,
  type AnalyticsTestResult,
} from "@/lib/analytics-test.functions";
import { runAiDiagnostic, type AiDiagnosticResult } from "@/lib/ai-diagnostic.functions";
import { getOcrConfigSnapshot, type OcrConfigSnapshot } from "@/lib/ocr-config.functions";


export const Route = createFileRoute("/admin/launch-checklist")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Launch Checklist" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["admin"]}>
      <LaunchChecklistPage />
    </AuthGate>
  ),
});

function LaunchChecklistPage() {
  const { isStaff, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking access…
        </div>
      </Shell>
    );
  }

  if (!isStaff && !isAdmin) {
    return (
      <Shell>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2 text-sm font-semibold text-navy">
            <ShieldAlert className="h-4 w-4 text-warn" /> Restricted
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            The launch checklist is available to staff and administrators only.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex text-xs font-semibold uppercase tracking-wider text-trust hover:underline"
          >
            Back to dashboard
          </Link>
        </div>
      </Shell>
    );
  }

  return <Checklist />;
}

function Checklist() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-launch-checklist"],
    queryFn: () => getLaunchChecklist(),
    staleTime: 30_000,
  });

  return (
    <Shell>
      <AdminPageHeader
        kicker="Admin · Diagnostics"
        title="Launch checklist"
        description="Internal snapshot of production integrations and compliance surface."
        actions={
          <AdminButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            loading={isFetching}
          >
            Refresh
          </AdminButton>
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Gathering status…
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-card p-6 shadow-card text-sm text-destructive">
          Couldn't load the checklist. Please try again.
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {data.groups.map((group) => (
            <AdminCard key={group.title}>
              <AdminCardHeader title={group.title} />
              <ul className="divide-y divide-border">
                {group.items.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-start justify-between gap-4 py-3"
                  >
                    <div className="flex items-start gap-3">
                      {item.ok ? (
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-savings" />
                      ) : (
                        <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground/60" />
                      )}
                      <div>
                        <div className="text-sm font-semibold text-navy">{item.label}</div>
                        {item.detail && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {item.detail}
                          </div>
                        )}
                      </div>
                    </div>
                    <AdminBadge tone={item.ok ? "success" : "muted"}>
                      {item.ok ? "Ready" : "Pending"}
                    </AdminBadge>
                  </li>
                ))}
              </ul>
            </AdminCard>
          ))}

          <MonitoringTestPanel onDone={() => refetch()} />
          <EmailDiagnosticPanel onDone={() => refetch()} />
          <OcrConfigPanel />
          <OcrDiagnosticPanel onDone={() => refetch()} />
          <LocationDiagnosticPanel onDone={() => refetch()} />
          <AnalyticsTestPanel onDone={() => refetch()} />
          <AiDiagnosticPanel onDone={() => refetch()} />





          <p className="text-xs text-muted-foreground">
            Snapshot generated {new Date(data.generatedAt).toLocaleString()}.
          </p>
        </div>
      )}
    </Shell>
  );
}

function MonitoringTestPanel({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "done"; result: MonitoringTestResult }
  >({ status: "idle" });

  const run = async () => {
    setState({ status: "running" });
    try {
      const result = await runMonitoringTest();
      setState({ status: "done", result });
    } catch (err) {
      setState({
        status: "done",
        result: {
          ok: false,
          provider: "unknown",
          dsnConfigured: false,
          message: err instanceof Error ? err.message : "unknown error",
          at: new Date().toISOString(),
        },
      });
    } finally {
      onDone();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg text-navy">Monitoring test</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Sends a harmless test event through the configured error monitor.
        The Monitoring row turns green once this succeeds.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.status === "running"}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {state.status === "running" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Send test error
        </button>
        {state.status === "done" && (
          <div className="flex flex-col gap-1 text-xs">
            <span
              className={
                state.result.ok ? "text-savings" : "text-destructive"
              }
            >
              {state.result.ok ? "✓ " : "✗ "}
              {state.result.message}
            </span>
            <span className="text-muted-foreground">
              provider={state.result.provider}
              {state.result.environment ? ` · env=${state.result.environment}` : ""}
              {state.result.release ? ` · release=${state.result.release}` : ""}
              {state.result.latencyMs != null ? ` · ${state.result.latencyMs}ms` : ""}
              {state.result.eventId ? ` · event=${state.result.eventId.slice(0, 12)}…` : ""}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function OcrConfigPanel() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-ocr-config-snapshot"],
    queryFn: () => getOcrConfigSnapshot(),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg text-navy">OCR configuration</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Read-only view of the Google Document AI configuration and live
            diagnostic state. Private key is never exposed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-navy hover:bg-secondary/60 disabled:opacity-60"
        >
          {isFetching ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          Refresh
        </button>
      </div>

      {isLoading && (
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div className="mt-4 text-xs text-destructive">
          Couldn't load OCR configuration.
        </div>
      )}
      {data && (
        <div className="mt-4 space-y-4">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-[260px_1fr]">
            <ConfigRow
              label="GOOGLE_DOCUMENT_AI_CLIENT_EMAIL"
              value={data.clientEmail}
            />
            <ConfigRow
              label="GOOGLE_DOCUMENT_AI_PROJECT_ID"
              value={data.projectId}
            />
            <ConfigRow
              label="GOOGLE_DOCUMENT_AI_LOCATION"
              value={data.location}
            />
            <ConfigRow
              label="GOOGLE_DOCUMENT_AI_PROCESSOR_ID"
              value={data.processorId}
            />
            <ConfigRow
              label="Private key present"
              value={data.privateKeyConfigured ? "Yes" : "No"}
            />
          </dl>

          <p className="text-[11px] text-muted-foreground">
            Run the OCR diagnostic below for live OAuth, processor, and IAM status.
          </p>
        </div>
      )}
    </section>
  );
}

function ConfigRow({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="font-mono text-xs text-navy break-all">
        {value ?? <span className="text-muted-foreground">Not configured</span>}
      </dd>
    </>
  );
}

function DiagRow({
  label,
  status,
  tone,
}: {
  label: string;
  status: string;
  tone: "ok" | "fail" | "neutral";
}) {
  const cls =
    tone === "ok"
      ? "text-savings"
      : tone === "fail"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className={`text-xs font-semibold ${cls}`}>{status}</dd>
    </>
  );
}

function stepLabel(
  status: "unknown" | "pass" | "fail",
  passLabel: string,
  failLabel: string,
): string {
  if (status === "pass") return passLabel;
  if (status === "fail") return failLabel;
  return "—";
}

function stepTone(
  status: "unknown" | "pass" | "fail",
): "ok" | "fail" | "neutral" {
  if (status === "pass") return "ok";
  if (status === "fail") return "fail";
  return "neutral";
}

function processorStateLabel(state: string | null): string {
  if (!state) return "—";
  if (state === "NOT_FOUND") return "NOT FOUND";
  return state;
}

function processorStateTone(
  state: string | null,
): "ok" | "fail" | "neutral" {
  if (state === "ENABLED") return "ok";
  if (!state) return "neutral";
  return "fail";
}

function OcrDiagnosticPanel({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "done"; result: OcrDiagnosticResult }
  >({ status: "idle" });

  const run = async () => {
    setState({ status: "running" });
    try {
      const result = await runOcrDiagnostic();
      setState({ status: "done", result });
    } catch (err) {
      setState({
        status: "done",
        result: {
          ok: false,
          provider: "unknown",
          configured: false,
          processorState: null,
          processorType: null,
          latencyMs: null,
          message: err instanceof Error ? err.message : "unknown error",
          at: new Date().toISOString(),
        },
      });
    } finally {
      onDone();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg text-navy">OCR diagnostic</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Verifies Google Document AI credentials, IAM, and that the processor
        is reachable + ENABLED. The OCR row turns green once this succeeds.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.status === "running"}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {state.status === "running" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Run OCR diagnostic
        </button>
        {state.status === "done" && (
          <span
            className={`text-xs ${
              state.result.ok ? "text-savings" : "text-destructive"
            }`}
          >
            {state.result.ok ? "✓ " : "✗ "}
            {state.result.message}
            {state.result.latencyMs != null ? ` (${state.result.latencyMs}ms)` : ""}
          </span>
        )}
      </div>
    </section>
  );
}

function EmailDiagnosticPanel({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "done"; result: EmailDiagnosticResult }
  >({ status: "idle" });

  const run = async () => {
    setState({ status: "running" });
    try {
      const result = await runEmailDiagnostic();
      setState({ status: "done", result });
    } catch (err) {
      setState({
        status: "done",
        result: {
          ok: false,
          provider: "lovable-emails",
          latencyMs: null,
          messageId: null,
          recipientDomain: null,
          message: err instanceof Error ? err.message : "unknown error",
          at: new Date().toISOString(),
        },
      });
    } finally {
      onDone();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg text-navy">Email diagnostic</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Sends a real welcome email to your own admin address through the
        production Lovable Emails send route (auth, suppression check,
        unsubscribe token, queue enqueue). The Email row turns green once
        this succeeds. Recipient is never logged.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.status === "running"}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {state.status === "running" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Run email diagnostic
        </button>
        {state.status === "done" && (
          <span
            className={`text-xs ${
              state.result.ok ? "text-savings" : "text-destructive"
            }`}
          >
            {state.result.ok ? "✓ " : "✗ "}
            {state.result.message}
            {state.result.recipientDomain
              ? ` — recipient domain: ${state.result.recipientDomain}`
              : ""}
          </span>
        )}
      </div>
    </section>
  );
}


function LocationDiagnosticPanel({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "done"; result: LocationDiagnosticResult }
  >({ status: "idle" });

  const run = async () => {
    setState({ status: "running" });
    try {
      const result = await runLocationDiagnostic();
      setState({ status: "done", result });
    } catch (err) {
      setState({
        status: "done",
        result: {
          ok: false,
          provider: "unknown",
          configured: false,
          verified: false,
          confidence: null,
          latencyMs: null,
          message: err instanceof Error ? err.message : "unknown error",
          at: new Date().toISOString(),
        },
      });
    } finally {
      onDone();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg text-navy">Location diagnostic</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Runs a fixed public test address through the configured location
        provider (Smarty / USPS / Google) and confirms it returns
        verified=true. The Location row turns green once this succeeds.
        Never uses customer data.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.status === "running"}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {state.status === "running" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Run location diagnostic
        </button>
        {state.status === "done" && (
          <span
            className={`text-xs ${
              state.result.ok ? "text-savings" : "text-destructive"
            }`}
          >
            {state.result.ok ? "✓ " : "✗ "}
            {state.result.message}
          </span>
        )}
      </div>
    </section>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AdminShell>
      <div className="mx-auto max-w-3xl">{children}</div>
    </AdminShell>
  );
}

function AnalyticsTestPanel({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "done"; result: AnalyticsTestResult }
  >({ status: "idle" });

  const run = async () => {
    setState({ status: "running" });
    try {
      const result = await runAnalyticsTest();
      setState({ status: "done", result });
    } catch (err) {
      setState({
        status: "done",
        result: {
          ok: false,
          provider: "unknown",
          credsConfigured: false,
          message: err instanceof Error ? err.message : "unknown error",
          at: new Date().toISOString(),
        },
      });
    } finally {
      onDone();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg text-navy">Analytics diagnostic</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Sends a real <code>diagnostic_test</code> event to the configured
        analytics provider. The Analytics row turns green once this succeeds.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.status === "running"}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {state.status === "running" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Send test event
        </button>
        {state.status === "done" && (
          <div className="flex flex-col gap-1 text-xs">
            <span
              className={
                state.result.ok ? "text-savings" : "text-destructive"
              }
            >
              {state.result.ok ? "✓ " : "✗ "}
              {state.result.message}
            </span>
            <span className="text-muted-foreground">
              provider={state.result.provider}
              {state.result.environment ? ` · env=${state.result.environment}` : ""}
              {state.result.release ? ` · release=${state.result.release}` : ""}
              {state.result.latencyMs != null ? ` · ${state.result.latencyMs}ms` : ""}
              {state.result.eventId ? ` · event=${state.result.eventId.slice(0, 12)}…` : ""}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function AiDiagnosticPanel({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "running" }
    | { status: "done"; result: AiDiagnosticResult }
  >({ status: "idle" });

  const run = async () => {
    setState({ status: "running" });
    try {
      const result = await runAiDiagnostic();
      setState({ status: "done", result });
    } catch (err) {
      setState({
        status: "done",
        result: {
          ok: false,
          provider: "lovable-ai-gateway",
          model: "unknown",
          latencyMs: null,
          promptTokens: null,
          completionTokens: null,
          totalTokens: null,
          message: err instanceof Error ? err.message : "unknown error",
          at: new Date().toISOString(),
        },
      });
    } finally {
      onDone();
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="font-display text-lg text-navy">AI diagnostic</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Sends a tiny prompt through Lovable AI Gateway and reports model,
        latency, and token usage. The AI Reasoning row turns green once this
        succeeds. No invoice text is ever sent.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.status === "running"}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-navy px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-navy/90 disabled:opacity-60"
        >
          {state.status === "running" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" />
          )}
          Run AI diagnostic
        </button>
        {state.status === "done" && (
          <div className="flex flex-col gap-1 text-xs">
            <span
              className={
                state.result.ok ? "text-savings" : "text-destructive"
              }
            >
              {state.result.ok ? "✓ " : "✗ "}
              {state.result.message}
            </span>
            <span className="text-muted-foreground">
              provider={state.result.provider}
              {state.result.model ? ` · model=${state.result.model}` : ""}
              {state.result.latencyMs != null ? ` · ${state.result.latencyMs}ms` : ""}
              {state.result.totalTokens != null
                ? ` · tokens=${state.result.totalTokens}`
                : ""}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

